import Network from '@/services/network';
import {
  ApiResponse,
  CreateUserRequest,
  CustomerDeletePermission,
  CreateUserResponse,
  DirectPermissionName,
  DirectPermissionStatus,
  PaginatedResponse,
  SearchParams,
  User,
} from '@/types';
import { ErrorResponse } from '@/utils';
import network from '@/services/network';

const getUsers = async (params: SearchParams): Promise<PaginatedResponse<User>> => {
  try {
    const { data } = await Network.get<PaginatedResponse<User>>('/users', {
      params,
    });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createUser = async (
  user: CreateUserRequest,
): Promise<CreateUserResponse> => {
  try {
    const { data } = await network.post<CreateUserResponse>('/users', user);

    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteUser = async (userId: number): Promise<void> => {
  try {
    await network.delete(`/users/${userId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateUser = async (
  userId: number,
  userData: Partial<User>,
): Promise<User> => {
  try {
    const { data } = await network.put<User>(`/users/${userId}`, userData);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const toggleUserStatus = async (
  userId: number,
  isActive: boolean,
): Promise<User> => {
  try {
    const { data } = await network.put<User>(`/users/${userId}`, {
      is_active: isActive,
    });
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const sendPasswordReset = async (userId: number): Promise<void> => {
  try {
    // Nutzt den bestehenden Password-Broker im Backend (gleiche Reset-Mail
    // wie der Self-Service "Passwort vergessen"-Flow). Kein Klartext-Passwort.
    await network.post(`/users/${userId}/send-password-reset`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateProfile = async (
  profileData: Partial<User>,
): Promise<User> => {
  try {
    const { data } = await network.put<User>('/profile', profileData);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Eigenes Passwort aendern (FB Thorluemke 05.08.). Serverseitig wird das
 * aktuelle Passwort geprueft; alle ANDEREN Sitzungen werden dabei beendet,
 * die eigene bleibt bestehen.
 */
const changePassword = async (payload: {
  current_password: string;
  password: string;
  password_confirmation: string;
}): Promise<void> => {
  try {
    await network.post('/auth/change-password', payload);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

// Batch-Zoom-Provisionierungsstatus fuer die uebergebenen User-IDs. Lazy
// nachgeladen (separat von /users), damit die Liste nicht auf die
// Zoom-API-Pruefung wartet. Liefert eine Map userId -> provisioniert (bool).
const getZoomStatus = async (
  ids: number[],
): Promise<Record<number, boolean>> => {
  try {
    const { data } = await Network.get<{ data: Record<number, boolean> }>(
      '/users/zoom-status',
      { params: { ids: ids.join(',') } },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * DSGVO-Loeschberechtigung eines einzelnen Users (Entscheid Alex
 * 07.09.2026, Revision zu Decision #190). Backend-Gate: nur SA
 * (UserPolicy::manageCustomerDeletePermission) — auch fuers Lesen.
 */
const getCustomerDeletePermission = async (
  userId: number,
): Promise<CustomerDeletePermission> => {
  try {
    const { data } = await Network.get<{ data: CustomerDeletePermission }>(
      `/users/${userId}/customer-delete-permission`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Kein optimistisches Setzen im Aufrufer: der neue Zustand kommt aus der
 * Antwort zurueck, damit ein abgelehnter Write (403, oder 422 beim SA) nicht
 * als gespeichert erscheint. Gleiches Muster wie setUserOverride.
 */
const setCustomerDeletePermission = async (
  userId: number,
  granted: boolean,
): Promise<CustomerDeletePermission> => {
  try {
    const { data } = await network.put<{ data: CustomerDeletePermission }>(
      `/users/${userId}/customer-delete-permission`,
      { granted },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Generischer Sonderrecht-Schalter (Entscheid Alex 11.09.2026): dasselbe
 * Backend-Gate wie beim Löschrecht (nur SA, auch fürs Lesen), ein unbekannter
 * Permission-Name ist serverseitig 404.
 */
const getDirectPermission = async (
  userId: number,
  permission: DirectPermissionName,
): Promise<DirectPermissionStatus> => {
  try {
    const { data } = await Network.get<{ data: DirectPermissionStatus }>(
      `/users/${userId}/direct-permissions/${permission}`,
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/** Kein optimistisches Setzen — Zustand kommt aus der Antwort (wie oben). */
const setDirectPermission = async (
  userId: number,
  permission: DirectPermissionName,
  granted: boolean,
): Promise<DirectPermissionStatus> => {
  try {
    const { data } = await network.put<{ data: DirectPermissionStatus }>(
      `/users/${userId}/direct-permissions/${permission}`,
      { granted },
    );
    return data.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const user = {
  getUsers,
  createUser,
  deleteUser,
  updateUser,
  toggleUserStatus,
  sendPasswordReset,
  updateProfile,
  changePassword,
  getZoomStatus,
  getCustomerDeletePermission,
  setCustomerDeletePermission,
  getDirectPermission,
  setDirectPermission,
};

export default user;
